import ApiService, { IRequestParameters } from '@/plugins/api-plugin/services/api.service'
import PurchaseOrderSummaryDao from '@/interfaces/order/PurchaseOrderSummary.dao'
import PurchaseOrderDetailDao, { PurchaseOrderLineDao } from '@/interfaces/order/PurchaseOrderDetail.dao'
import PurchaseOrderDeliveriesDao, {
    PurchaseOrderDeliveryLineDao,
} from '@/interfaces/order/PurchaseOrderDeliveries.dao'
import PurchaseOrderLookupsDao from '@/interfaces/order/PurchaseOrderLookups.dao'
import PurchaseOrderMaterialDao from '@/interfaces/order/PurchaseOrderMaterial.dao'
import PurchaseOrderReviewersDao from '@/interfaces/order/PurchaseOrderReviewers.dao'
import {
    PurchaseOrderRequestApprovalDto,
    PurchaseOrderReviewDto,
    PurchaseOrderWriteDto,
} from '@/interfaces/order/PurchaseOrderWrite.dto'
import {
    PurchaseOrderConfirmReceiptDto,
    PurchaseOrderRemoveDeliveryLinesDto,
} from '@/interfaces/order/PurchaseOrderDeliveriesWrite.dto'
import { PageNumberPagination } from '@/interfaces/common/PageNumberPagination'
import { AxiosResponse } from 'axios'

export default class PurchaseOrdersService extends ApiService {
    async getPurchaseOrders(
        params?: IRequestParameters,
    ): Promise<[PurchaseOrderSummaryDao[], PageNumberPagination] | null> {
        return this.request<undefined, [PurchaseOrderSummaryDao[], PageNumberPagination] | null>(
            'GET',
            '/purchase-orders',
            params,
            undefined,
            undefined,
            'getPurchaseOrders',
        ).catch(() => null)
    }

    async getPurchaseOrderLookups(): Promise<PurchaseOrderLookupsDao | null> {
        return this.request<undefined, PurchaseOrderLookupsDao>(
            'GET',
            '/purchase-orders/lookups',
            undefined,
            undefined,
            undefined,
            'getPurchaseOrderLookups',
        ).catch(() => null)
    }

    async getPurchaseOrder(id: number, includeLines = false): Promise<PurchaseOrderDetailDao | null> {
        return this.request<undefined, PurchaseOrderDetailDao>(
            'GET',
            `/purchase-orders/${id}`,
            includeLines ? { includeLines: 'true' } : undefined,
            undefined,
            undefined,
            'getPurchaseOrder',
        ).catch(() => null)
    }

    async getPurchaseOrderLineItems(
        id: number,
        params?: IRequestParameters,
    ): Promise<[PurchaseOrderLineDao[], PageNumberPagination] | null> {
        return this.request<undefined, [PurchaseOrderLineDao[], PageNumberPagination] | null>(
            'GET',
            `/purchase-orders/${id}/line-items`,
            params,
            undefined,
            undefined,
            'getPurchaseOrderLineItems',
        ).catch(() => null)
    }

    async getPurchaseOrderDeliveries(id: number): Promise<PurchaseOrderDeliveriesDao | null> {
        return this.request<undefined, PurchaseOrderDeliveriesDao>(
            'GET',
            `/purchase-orders/${id}/deliveries`,
            undefined,
            undefined,
            undefined,
            'getPurchaseOrderDeliveries',
        ).catch(() => null)
    }

    async getPurchaseOrderDeliveryLines(
        id: number,
        params?: IRequestParameters,
    ): Promise<[PurchaseOrderDeliveryLineDao[], PageNumberPagination] | null> {
        return this.request<undefined, [PurchaseOrderDeliveryLineDao[], PageNumberPagination] | null>(
            'GET',
            `/purchase-orders/${id}/deliveries/lines`,
            params,
            undefined,
            undefined,
            'getPurchaseOrderDeliveryLines',
        ).catch(() => null)
    }

    async confirmPurchaseOrderReceipt(
        id: number,
        body: PurchaseOrderConfirmReceiptDto,
    ): Promise<PurchaseOrderDeliveriesDao | null> {
        return this.request<PurchaseOrderConfirmReceiptDto, PurchaseOrderDeliveriesDao>(
            'POST',
            `/purchase-orders/${id}/deliveries/confirm`,
            undefined,
            body,
            undefined,
            'confirmPurchaseOrderReceipt',
        ).catch(() => null)
    }

    async exportPurchaseOrderDeliveries(id: number, format: string): Promise<AxiosResponse | null> {
        return this.request<undefined, AxiosResponse>(
            'GET',
            `/purchase-orders/${id}/deliveries/export`,
            { format },
            undefined,
            'blob',
            'exportPurchaseOrderDeliveries',
        ).catch(() => null)
    }

    async terminatePurchaseOrder(id: number): Promise<PurchaseOrderDeliveriesDao | null> {
        return this.request<undefined, PurchaseOrderDeliveriesDao>(
            'POST',
            `/purchase-orders/${id}/deliveries/terminate`,
            undefined,
            undefined,
            undefined,
            'terminatePurchaseOrder',
        ).catch(() => null)
    }

    async removePurchaseOrderDeliveryLines(
        id: number,
        body: PurchaseOrderRemoveDeliveryLinesDto,
    ): Promise<PurchaseOrderDeliveriesDao | null> {
        return this.request<PurchaseOrderRemoveDeliveryLinesDto, PurchaseOrderDeliveriesDao>(
            'POST',
            `/purchase-orders/${id}/deliveries/remove-lines`,
            undefined,
            body,
            undefined,
            'removePurchaseOrderDeliveryLines',
        ).catch(() => null)
    }

    async searchPurchaseOrderMaterials(params: IRequestParameters): Promise<PurchaseOrderMaterialDao[] | null> {
        return this.request<undefined, PurchaseOrderMaterialDao[]>(
            'GET',
            '/purchase-orders/materials',
            params,
            undefined,
            undefined,
            'searchPurchaseOrderMaterials',
        ).catch(() => null)
    }

    async createPurchaseOrder(body: PurchaseOrderWriteDto): Promise<PurchaseOrderDetailDao | null> {
        return this.request<PurchaseOrderWriteDto, PurchaseOrderDetailDao>(
            'POST',
            '/purchase-orders',
            undefined,
            body,
            undefined,
            'createPurchaseOrder',
        ).catch(() => null)
    }

    async updatePurchaseOrder(id: number, body: PurchaseOrderWriteDto): Promise<PurchaseOrderDetailDao | null> {
        return this.request<PurchaseOrderWriteDto, PurchaseOrderDetailDao>(
            'PUT',
            `/purchase-orders/${id}`,
            undefined,
            body,
            undefined,
            'updatePurchaseOrder',
        ).catch(() => null)
    }

    async requestPurchaseOrderApproval(
        id: number,
        body: PurchaseOrderRequestApprovalDto,
    ): Promise<PurchaseOrderDetailDao | null> {
        return this.request<PurchaseOrderRequestApprovalDto, PurchaseOrderDetailDao>(
            'POST',
            `/purchase-orders/${id}/request-approval`,
            undefined,
            body,
            undefined,
            'requestPurchaseOrderApproval',
        ).catch(() => null)
    }

    async getPurchaseOrderReviewers(id: number): Promise<PurchaseOrderReviewersDao | null> {
        return this.request<undefined, PurchaseOrderReviewersDao>(
            'GET',
            `/purchase-orders/${id}/reviewers`,
            undefined,
            undefined,
            undefined,
            'getPurchaseOrderReviewers',
        ).catch(() => null)
    }

    async approvePurchaseOrder(id: number, body: PurchaseOrderReviewDto): Promise<PurchaseOrderDetailDao | null> {
        return this.request<PurchaseOrderReviewDto, PurchaseOrderDetailDao>(
            'POST',
            `/purchase-orders/${id}/approve`,
            undefined,
            body,
            undefined,
            'approvePurchaseOrder',
        ).catch(() => null)
    }

    async rejectPurchaseOrder(id: number, body: PurchaseOrderReviewDto): Promise<PurchaseOrderDetailDao | null> {
        return this.request<PurchaseOrderReviewDto, PurchaseOrderDetailDao>(
            'POST',
            `/purchase-orders/${id}/reject`,
            undefined,
            body,
            undefined,
            'rejectPurchaseOrder',
        ).catch(() => null)
    }

    async issuePurchaseOrder(id: number): Promise<PurchaseOrderDetailDao | null> {
        return this.request<undefined, PurchaseOrderDetailDao>(
            'POST',
            `/purchase-orders/${id}/issue`,
            undefined,
            undefined,
            undefined,
            'issuePurchaseOrder',
        ).catch(() => null)
    }

    async issuePurchaseOrders(
        ids: number[],
    ): Promise<{ issuedIds: number[]; failedId: number | null; message: string | null } | null> {
        return this.request<
            { ids: number[] },
            { issuedIds: number[]; failedId: number | null; message: string | null }
        >('POST', '/purchase-orders/issue', undefined, { ids }, undefined, 'issuePurchaseOrders').catch(() => null)
    }

    async deletePurchaseOrder(id: number): Promise<{ success: boolean } | null> {
        return this.request<undefined, { success: boolean }>(
            'DELETE',
            `/purchase-orders/${id}`,
            undefined,
            undefined,
            undefined,
            'deletePurchaseOrder',
        ).catch(() => null)
    }

    async deletePurchaseOrderLine(id: number, expectedLineId: number): Promise<PurchaseOrderDetailDao | null> {
        return this.request<undefined, PurchaseOrderDetailDao>(
            'DELETE',
            `/purchase-orders/${id}/lines/${expectedLineId}`,
            undefined,
            undefined,
            undefined,
            'deletePurchaseOrderLine',
        ).catch(() => null)
    }
}
